/**
 * Offer & lander weight optimization (per path). Independent of path-level
 * traffic distribution AI in the left column.
 */

import type { FC } from 'react';
import { HelpCircle } from 'lucide-react';
import type { VoluumPathUpdate } from '@trafficpulse/shared';

import { Tooltip } from '@/components/ui/Tooltip';
import { getOffersLandersAi, setOffersLandersAi } from '@/features/flow-optimizer/tabs/standard/v2/utils/path-ai';
import { Checkbox } from '@/components/ui/form/controls/Checkbox';
import { Switch } from '@/components/ui/form/controls/Switch';
import { cn } from '@/lib/utils';
import { useTrackerAiHidden } from '@/features/flow-optimizer/tracker';

export interface PathSettingsDistributionTopProps {
  path: VoluumPathUpdate;
  onPatch: (mutator: (p: VoluumPathUpdate) => void) => void;
  compact?: boolean;
}

const PathSettingsDistributionTop: FC<PathSettingsDistributionTopProps> = ({
  path,
  onPatch,
  compact = false,
}) => {
  // Hide the tracker's offer/lander optimization toggle while TrafficPulse
  // Optimisation (preview) is active — auto-managed then — and for a tracker
  // without its own AI. Render-only; the draft/save payload is unchanged.
  const hideAi = useTrackerAiHidden();
  return (
  <div
    className={cn(
      'flex flex-wrap items-center justify-between',
      compact ? 'gap-2' : 'gap-3',
    )}
  >
    {!hideAi ? (
    <label
      className={cn(
        'flex cursor-pointer items-center gap-2',
        compact ? 'text-xs' : 'text-sm',
      )}
    >
      <Switch
        checked={getOffersLandersAi(path)}
        onCheckedChange={(v) =>
          onPatch((p) => {
            setOffersLandersAi(p, v);
          })
        }
        aria-label="Offer and lander weight optimization for this path"
      />
      <span className="font-medium">Offer / lander optimization</span>
    </label>
    ) : null}
    <label
      className={cn(
        'flex cursor-pointer items-center gap-2',
        compact ? 'text-xs' : 'text-sm',
      )}
    >
      <Checkbox
        checked={Boolean(path.smartRotation)}
        onCheckedChange={(v) =>
          onPatch((p) => {
            p.smartRotation = v === true;
          })
        }
        aria-label="Smart rotation"
      />
      <span>Smart rotation</span>
      <Tooltip
        content="Rotates landers/offers using Voluum smart rotation when weight AI is active."
        side="top"
      >
        <HelpCircle
          className="h-3 w-3 shrink-0 text-muted-foreground"
          aria-label="About smart rotation"
        />
      </Tooltip>
    </label>
  </div>
  );
};

export default PathSettingsDistributionTop;
